<template>
  <div>
    <v-card class="session-register session__container">
      <ChooseLanguage :positionAbsolute="true" />
      <div class="session__content">
        <div>
          <v-img
            src="@/static/brand_logo.svg"
            class="mx-auto session__logo"
            aspect-ratio
            width="300"
          >
          </v-img>
        </div>
        <template v-if="userFromInvitation">
          <h1 class="text-center session__title">
            {{ $t(`message.common.setPasswordAndFinishRegistration`) }}
          </h1>
        </template>
        <template v-else-if="isFreeTrial">
          <h1 class="text-center session__title">
            {{ $t(`message.common.signUpTrial`) }}
          </h1>
          <h2
            class="text-center session__sub-title mb-10"
            v-html="$t(`message.common.signUpTrialTitle`, [trialDuration])"
          ></h2>
        </template>
        <template v-else-if="isEnterpriseInvitation">
          <h1 class="text-center session__title">
            {{ $t(`message.common.signUpTrial`) }}
          </h1>
        </template>
        <template v-else>
          <h1 class="text-center session__title">
            {{ $t(`message.common.signUp`) }}
          </h1>
          <h2 class="text-center session__sub-title mb-10">
            {{ $t(`message.common.signUpTitle`) }}
          </h2>
        </template>
        <v-form
          v-model="valid"
          ref="form"
          lazy-validation
          @keyup.native.enter="submit"
          class="mb-4"
        >
          <v-text-field
            class="input-field"
            :label="emailLabel + ' *'"
            v-model="email"
            color="primary"
            v-validate="'required|email'"
            :error-messages="errors.collect(emailLabel)"
            :data-vv-name="emailLabel"
            required
            :disabled="userFromInvitation"
          ></v-text-field>
          <v-text-field
            class="input-field"
            :label="passwordLabel + ' *'"
            v-model="password"
            color="primary"
            v-validate="'required|min:9'"
            :error-messages="errors.collect(passwordLabel)"
            :data-vv-name="passwordLabel"
            type="password"
            required
          ></v-text-field>
          <v-text-field
            class="input-field"
            :label="`${phoneNumberLabel}`"
            v-model="phoneNumber"
            color="primary"
            v-validate="'max:15'"
            :error-messages="errors.collect(phoneNumberLabel)"
            :data-vv-name="phoneNumberLabel"
            required
          ></v-text-field>
          <v-checkbox
            v-for="consent in consents"
            v-model="consent.value"
            :key="consent.id"
            :rules="[
              v => consent.optional || !!v || $t('message.error.requiredField')
            ]"
            color="primary"
            class="mt-0"
            :required="!consent.optional"
          >
            <template #label>
              <p class="consent">
                <span v-if="consent.optional" class="consent--optional">
                  {{ `(${$t("message.common.optional")})` }}
                </span>
                {{ consent.label }}
              </p>
            </template>
          </v-checkbox>

          <i18n
            path="message.session.register.bySigningUpYouAgreeTo"
            tag="p"
            class="register__info"
          >
            <template v-slot:privacyPolicy>
              <a
                :href="privacyPolicyLink.path"
                rel="noopener noreferrer"
                target="_blank"
              >
                {{ privacyPolicyLink.name }}
              </a>
            </template>
            <template v-slot:regulations>
              <a
                :href="termsOfUseLink.path"
                rel="noopener noreferrer"
                target="_blank"
              >
                {{ termsOfUseLink.name }}
              </a>
            </template>
          </i18n>
          <div class="content__actions">
            <vue-recaptcha
              ref="recaptcha"
              :sitekey="reCaptchaSiteKey"
              :loadRecaptchaScript="true"
              badge="bottomleft"
              @verify="onCaptchaVerified"
              @expired="onCaptchaExpired"
            >
              <v-btn
                large
                block
                color="primary"
                :loading="submitting"
                @click="submitting = true"
              >
                {{ $t(`message.common.signUp`) }}
              </v-btn>
            </vue-recaptcha>
          </div>
          <!-- <div class="pt-5">
          <v-btn large block dark :color="facebookColor" class="mb-3">
            <v-icon left>mdi-facebook</v-icon
            >{{ $t("message.session.signUpWithFacebook") }}
          </v-btn>
          <v-btn large block dark :color="googleColor" class="mb-3">
            <v-icon left>icon-google</v-icon>
            {{ $t("message.session.signUpWithGoogle") }}
          </v-btn>
        </div> -->
          <p class="text-center mt-12">
            {{ $t("message.session.haveAccount") }}
            <router-link :to="{ name: 'session-login' }">{{
              $t("message.common.login")
            }}</router-link>
          </p>
        </v-form>
      </div>
    </v-card>
    <SectionLoader :status="isLoading" :opacity="1" />
  </div>
</template>

<script>
import ChooseLanguage from "@/components/AppBar/ChooseLanguage.vue";
import SectionLoader from "@/components/Loader/SectionLoader.vue";

import { LOCALE } from "@/enums/locale";
import { GTM_EVENT_NAME } from "@/enums/gtm-events";
import constants from "@/config/constants.js";
import VueRecaptcha from "vue-recaptcha";
import cuid from "cuid";
import { googleApiConfig } from "@/config/google-api";
import { freeTrialDaysMixin } from "@/helpers/mixins/free-trial-days";
import invitationLinksService from "@/services/invitation-links";
import serviceHandler from "@/helpers/handlers/service";

export default {
  name: "SessionSignUp",
  components: {
    VueRecaptcha,
    ChooseLanguage,
    SectionLoader
  },
  mixins: [freeTrialDaysMixin],
  data() {
    return {
      isLoading: false,
      reCaptchaSiteKey: googleApiConfig.reCaptchaSiteKey,
      submitting: false,
      valid: false,
      email: "",
      password: "",
      phoneNumber: "",
      enterpriseInvitation: "",
      passwordLabel: this.$t("message.common.password"),
      emailLabel: this.$t("message.common.email"),
      phoneNumberLabel: this.$t("message.common.phone_number"),
      appBrand: constants.appBrand,
      facebookColor: constants.facebookColor,
      googleColor: constants.googleColor,
      regulations: {
        name: this.$t("message.session.register.regulations"),
        path: { en: constants.regulationsEn, pl: constants.regulations }
      },
      privacyPolicy: {
        name: this.$t("message.session.register.privacyPolicy"),
        path: { en: constants.privacyPolicyEn, pl: constants.privacyPolicy }
      },
      consents: [
        // {
        //   optional: false,
        //   label: this.$t("message.session.register.consent_1"),
        //   value: false,
        //   id: cuid()
        // },
        // {
        //   optional: false,
        //   label: this.$t("message.session.register.consent_2"),
        //   value: false,
        //   id: cuid()
        // },
        {
          optional: true,
          label: this.$t("message.session.register.consent_3"),
          value: false,
          id: cuid()
        }
      ]
    };
  },

  computed: {
    userFromInvitation() {
      return (
        !!this.$route.query.id &&
        !!this.$route.query.email &&
        !!this.$route.query.key
      );
    },
    currentLanguage() {
      return this.$i18n.locale;
    },
    isEnterpriseInvitation() {
      return !!this.$route.query.enterprise_invitation;
    },

    isFreeTrial() {
      return (
        this.$route.query.is_free_trial !== "false" &&
        !this.isEnterpriseInvitation
      );
    },
    termsOfUseLink() {
      return {
        name: this.regulations.name,
        path: this.regulations.path[this.currentLanguage]
      };
    },
    privacyPolicyLink() {
      return {
        name: this.privacyPolicy.name,
        path: this.privacyPolicy.path[this.currentLanguage]
      };
    }
  },
  async created() {
    if (
      this.$route.query.audit === "true" &&
      this.$route.query.custom_invitation === "true"
    ) {
      this.isLoading = true;

      try {
        const { invitation_link } = await serviceHandler(
          invitationLinksService.getInvitationLink({
            lang: this.currentLanguage
          })
        );
        window.location.href = invitation_link;
      } catch (error) {
        window.location.href = "https://center.ai/pl/cennik/";
      } finally {
        this.isLoading = false;
      }
      return;
    }
    if (this.userFromInvitation || this.isEnterpriseInvitation) {
      this.email = this.$route.query.email;
    } else if (!this.$route.query.price_list && !this.isEnterpriseInvitation) {
      this.isLoading = true;
      window.location.href = "https://center.ai/pl/cennik/";
    }
    if (this.isEnterpriseInvitation) {
      this.enterpriseInvitation = this.$route.query.enterprise_invitation;
    }
  },

  mounted() {
    document
      .querySelectorAll(".input-field label")
      .forEach(el => el.classList.add("v-label--active"));
  },

  methods: {
    onCaptchaVerified() {
      this.$refs.recaptcha.reset();
      this.submit();
    },

    onCaptchaExpired() {
      this.$refs.recaptcha.reset();
    },

    async submit() {
      this.submitting = true;

      if (this.$refs.form.validate()) {
        this.snackbar = true;
      }
      await this.$validator.validateAll().then(async isTrue => {
        if (isTrue && this.valid) {
          await this.signUp();
        }
      });

      this.submitting = false;
    },

    async signUp() {
      this.submitting = true;
      if (this.userFromInvitation) {
        await this.$store.dispatch("auth/acceptInvitation", {
          email: this.email,
          user: this.$route.query.id,
          phone_number: this.phoneNumber,
          password: this.password,
          is_marketing_consent: this.consents[0].value,
          key: this.$route.query.key,
          language: this.$store.getters["settings/selectedLocale"]
        });
      } else {
        await this.$store.dispatch("auth/signUpWithLoginAndPassword", {
          email: this.email,
          phone_number: this.phoneNumber,
          password: this.password,
          is_marketing_consent: this.consents[0].value,
          language: this.$store.getters["settings/selectedLocale"],
          enterprise_invitation: this.enterpriseInvitation
        });
      }

      if (this.$store.getters["auth/isAuthenticated"]) {
        const selectedLocale = this.$store.getters["settings/selectedLocale"];

        this.$gtm.trackCustomEvent({
          event: GTM_EVENT_NAME.NEW_USER_SIGN_UP,
          app_lang: selectedLocale || LOCALE.PL,
          email: this.email
        });

        await this.$router.push(
          this.userFromInvitation
            ? { path: "/" }
            : { name: "wizard", query: this.$route.query }
        );
      } else {
        this.submitting = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.consent {
  @include font-size(12px);
  color: $dark;
  margin-bottom: 0;
}

.content {
  &__actions {
    display: flex;
    gap: $spacing * 2;
    margin-top: $spacing * 4.5;

    & > * {
      flex: 1;
    }
  }
}
.consent--optional {
  color: $primary;
}

.register__info {
  @include font-size(12px);
  color: $dark;
}
</style>
